import { ParticipationRateMaintenanceConstant } from "presentation/constant/ParticipationRate/ParticipationRateMaintenanceConstant";
import { useParticipationRateMaintenanceVM } from "presentation/hook/ParticipationRate/useParticipationRateMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useParticipationRateMaintenanceTracked } from "presentation/store/ParticipationRate/ParticipationRateMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHBreadcrumb, HPHCheckbox, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Breadcrumb, SidebarCaption, Sidebarheader, SidebarTitle, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

export const ParticipationRateMaintenanceFormPanel = () => {
    const [participationRateState] = useParticipationRateMaintenanceTracked();
    const participationRateVM = useParticipationRateMaintenanceVM();
    const { editingEntity, isEdit, isRead, searchCriteria } = participationRateState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const SCREEN_CONSTANT = ParticipationRateMaintenanceConstant;
    const [anaInfoState] = useANAInfoTracked();
    const { allowUpdate, allowCreate } = anaInfoState;

    const handleEdit = useCallback(() => {
        participationRateVM.onEditClick();
    }, [participationRateVM]);

    const handleReset = useCallback(() => {
        participationRateVM.onResetClick();
    }, [participationRateVM]);

    const handleCancelClick = useCallback(() => {
        participationRateVM.onCancel();
    }, [participationRateVM])

    const handleSaveClick = useCallback(async () => {
        setIsLoading(true);
        await participationRateVM.onSave(editingEntity).then(async (data) => {
            if (!data.success) {
                setIsLoading(false);
                messageBarVM.showError('Save Data Failed.');
                return;
            }
            await participationRateVM.onSearch(false, searchCriteria).then(() => {
                messageBarVM.showSuccess("Save Successfully.");
                participationRateVM.onCancel();
                setIsLoading(false);
            }).catch((error) => {
                setIsLoading(false);
            });
        }).catch(() => {
            setIsLoading(false);
            messageBarVM.showError('Save Data Failed.');
        });
    }, [participationRateVM, editingEntity, searchCriteria, messageBarVM])

    const memoConsortiumCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={isRead}
                isShowOptional={true}
                readOnlyValue={editingEntity?.consortiumCode || ''}
                fieldValue={editingEntity?.consortiumCode}
                fieldLabel={SCREEN_CONSTANT.CONSORTIUM_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'consortiumCode'}
                maxLength={20}
                options={participationRateState.dynamicOptions.consortiumCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    participationRateVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.CONSORTIUM_CODE, editingEntity?.consortiumCode, isRead, participationRateState.dynamicOptions.consortiumCodeDropdownOptions, participationRateVM])

    const memoSoa = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <NbisCommonField
                isReadOnly={isRead}
                isShowOptional={true}
                readOnlyValue={editingEntity?.soa || ''}
                fieldValue={editingEntity?.soa}
                fieldLabel={SCREEN_CONSTANT.SOA}
                fieldType={FieldType.TEXT}
                fieldKey={'soa'}
                maxLength={4}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    participationRateVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isRead, editingEntity?.soa, SCREEN_CONSTANT.SOA, participationRateVM])

    const memoVesselCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <NbisCommonField
                isReadOnly={isRead}
                isShowOptional={true}
                readOnlyValue={editingEntity?.vesselCode || ''}
                fieldValue={editingEntity?.vesselCode}
                fieldLabel={SCREEN_CONSTANT.VESSEL_CODE}
                fieldType={FieldType.TEXT}
                fieldKey={'vesselCode'}
                maxLength={6}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    participationRateVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isRead, editingEntity?.vesselCode, SCREEN_CONSTANT.VESSEL_CODE, participationRateVM])

    const memoVoyageCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <NbisCommonField
                isReadOnly={isRead}
                isShowOptional={true}
                readOnlyValue={editingEntity?.voyageCode || ''}
                fieldValue={editingEntity?.voyageCode}
                fieldLabel={SCREEN_CONSTANT.VOYAGE_CODE}
                fieldType={FieldType.TEXT}
                fieldKey={'voyageCode'}
                maxLength={10}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    participationRateVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isRead, editingEntity?.voyageCode, SCREEN_CONSTANT.VOYAGE_CODE, participationRateVM])

    const memoEtd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={isRead}
                optional={true}
                label={SCREEN_CONSTANT.ETD}
                width="185px"
                date={editingEntity?.etd ?? null}
                fieldName="etd"
                onDateChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    participationRateVM.onFieldChange(fieldKey, fieldValue)} />
        </div>
        , [isRead, SCREEN_CONSTANT.ETD, editingEntity?.etd, participationRateVM])

    const memoEtb = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <DatePickerComponent
                optional={true}
                disabled={isRead}
                label={SCREEN_CONSTANT.ETB}
                width="185px"
                date={editingEntity?.etb ?? null}
                fieldName="etb"
                onDateChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    participationRateVM.onFieldChange(fieldKey, fieldValue)} />
        </div>
        , [isRead, SCREEN_CONSTANT.ETB, editingEntity?.etb, participationRateVM])

    const memoContainerStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <NbisCommonField
                isReadOnly={isRead}
                isShowOptional={true}
                readOnlyValue={editingEntity?.cntrStatus || ''}
                fieldValue={editingEntity?.cntrStatus}
                fieldLabel={SCREEN_CONSTANT.CONTAINER_STATUS}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'cntrStatus'}
                maxLength={10}
                options={participationRateState.dynamicOptions.cntrStatusDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    participationRateVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isRead, editingEntity?.cntrStatus, SCREEN_CONSTANT.CONTAINER_STATUS, participationRateState.dynamicOptions.cntrStatusDropdownOptions, participationRateVM])


    const memoActiveInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <NbisCommonField
                fieldKey={"activeInd"}
                requiredFieldList={[]}
                fieldLabel={SCREEN_CONSTANT.ACTIVE_IND}
                fieldType={FieldType.TEXT}
                isReadOnly={true}
                onFieldChange={() => { }} />
            <HPHCheckbox
                label={""}
                disabled={isRead}
                checked={editingEntity?.activeInd === 'Y'}
                onChange={(e) => participationRateVM.onCheckboxChange(e.checked, "activeInd")}
            />
        </div>
        , [SCREEN_CONSTANT.ACTIVE_IND, isRead, editingEntity?.activeInd, participationRateVM])

    return (<>
        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>
            <Sidebarheader style={{ width: '100%' }}>
                {isLoading && <Loader Indicator="Spinner" size="Medium" />}
                <Breadcrumb>
                    <HPHBreadcrumb breadcrumbData={[{ title: SCREEN_CONSTANT.CATEGORY },
                    { title: SCREEN_CONSTANT.TITLE }]} onCurrentClick={() => { }}>
                    </HPHBreadcrumb>
                </Breadcrumb>
                <StyledAction>
                    <>
                        {allowUpdate && !(isEdit) && <NbisIconButton fileName='Icon-pen' size='medium' disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Edit'} onClick={handleEdit} />}
                        {(isEdit) && <IconButton fileName="Icon-reset" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Reset'} onClick={() => handleReset()} />}

                        <IconButton fileName="Icon-cross" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement='bottom' toolTipText={'Close'} onClick={() => handleCancelClick()} />
                        {allowCreate && (isEdit) && <IconButton fileName="Icon-tick" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Save'} onClick={() => handleSaveClick()} />}
                    </>
                </StyledAction>
            </Sidebarheader>

            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <div className={'im-flex-row'}>
                            <div className={'flex-row-item-stretch'}>
                                <SidebarTitle>
                                    {"Participation Rate Information"}
                                </SidebarTitle>
                                {<SidebarCaption>"All fields are required unless indicated as 'optional'"</SidebarCaption>}
                            </div>
                        </div>
                        <CriteriaItemContainer>
                            {memoConsortiumCode}
                            {memoSoa}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoVesselCode}
                            {memoVoyageCode}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoEtb}
                            {memoEtd}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoContainerStatus}
                            {memoActiveInd}
                        </CriteriaItemContainer>
                    </div>
                </div>
            </div>
        </StyledSidebar>
    </>
    )
}
